/**
 * useRefetchOnReturn - Auto-refetch data on tab focus or route return
 * 
 * P0 Feature: Ensures detail pages show fresh data when user returns
 * 
 * Triggers refetch when:
 * 1. Browser tab becomes visible (user returns from another tab)
 * 2. User navigates back to this page (route return)
 * 
 * Guards:
 * - Throttles to prevent spam (default 1500ms)
 * - Skips if already refreshing
 * - Skips if disabled
 * - Cleans up listeners on unmount
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const useRefetchOnReturn = ({
  refetch,                    // async () => void - function to call for refetch
  enabled = true,             // boolean - toggle to enable/disable
  refreshing = false,         // boolean - skip if already refreshing
  throttleMs = 1500,          // number - minimum ms between refetches
  onVisibilityChange = true,  // boolean - refetch when tab becomes visible
  onRouteReturn = true,       // boolean - refetch on navigation return
}) => {
  const lastFetchTimeRef = useRef(Date.now());
  const locationKeyRef = useRef(null);
  const mountedRef = useRef(true);
  const location = useLocation();

  // Throttled refetch function
  const throttledRefetch = useCallback(async () => {
    if (!enabled || refreshing || !mountedRef.current) {
      return;
    }

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    if (timeSinceLastFetch < throttleMs) {
      // Skip - too soon since last fetch
      return;
    }

    lastFetchTimeRef.current = now;

    try {
      await refetch();
    } catch (err) {
      console.error('useRefetchOnReturn: refetch failed', err);
    }
  }, [enabled, refreshing, throttleMs, refetch]);

  // Handle visibility change (tab focus)
  useEffect(() => {
    if (!onVisibilityChange) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        throttledRefetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onVisibilityChange, throttledRefetch]);

  // Handle route return (location key changes)
  useEffect(() => {
    if (!onRouteReturn) return;

    // Skip on initial mount
    if (locationKeyRef.current === null) {
      locationKeyRef.current = location.key;
      return;
    }

    // If location key changed, user navigated (possibly returned)
    if (locationKeyRef.current !== location.key) {
      locationKeyRef.current = location.key;
      throttledRefetch();
    }
  }, [location.key, onRouteReturn, throttledRefetch]);

  // Track mounted state for cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Return manual trigger for external use
  return {
    triggerRefetch: throttledRefetch,
  };
};

export default useRefetchOnReturn;
